import {
  View,
  RadioGroup,
  RadioGroupItem,
  Button,
  Label,
  Textarea,
} from "@/components/ui";
import {
  PackageDepartmentEnum,
  PackageDetailSortByEnum,
  PackageRecurrenceEnum,
  SortEnum,
} from "@/__generated__/graphql";
import { useAllClientPackageDetailsStore } from "@/store/booking/allClientPackageDetails";
import { useEffect, useMemo, useState } from "react";
import { useBookingState } from "@/store/booking/useBookingState";
import { ListHeading } from "@/components/shared/list-header";
import {
  ChatCircleText,
  Question,
  CaretDown,
  Cube,
  StackPlus,
} from "@/components/shared/icons";
import { getDeepCleaningPackageFromCode } from "@/constants/booking";
import { Typography } from "@/components/shared/typography";
import { BottomDrawerModal } from "@/components/shared/bottom-drawer-modal";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { ServiceRadioItem } from "@/components/shared/service-radio-item";
import { HelpCard } from "../pet-grooming-booking/help-card";
import { today } from "@/lib/helpers/date";
import { addDays } from "date-fns";
import { Message } from "@/components/shared/message";

export const DeepCleaningBooking = () => {
  const {
    data: {
      packageDetails,
      selectedPackageIds,
      creationReason,
      additionalNotes,
    },
    setBookingState,
  } = useBookingState();
  const { fetch: getAllClientPackageDetails } =
    useAllClientPackageDetailsStore();

  const [openServiceReasonModal, setOpenServiceReasonModal] = useState(false);
  const handleClickServiceReasonModal = () => {
    setOpenServiceReasonModal(true);
  };

  const currentPackages = useMemo(() => {
    return (
      packageDetails
        .map((pkg) => ({ ...pkg, ...getDeepCleaningPackageFromCode(pkg.code) }))
        .filter((pkg) => pkg.description)
        .sort((a, b) => a.order - b.order) || []
    );
  }, [packageDetails]);

  const currentPackageIDs = useMemo(() => {
    return currentPackages.map((pkg) => pkg.id);
  }, [currentPackages]);

  const onChangePackage = (id: string) => {
    const pkg = currentPackages.find((pkg) => pkg.id === id);
    if (!pkg) return;

    setBookingState({
      selectedPackageIds: [id],
      totalCleaner: pkg.numberOfCleaner,
    });
  };

  const deepCleaningStartDate = addDays(today(), 2);
  const deepCleaningEndDate = addDays(deepCleaningStartDate, 6);

  useEffect(() => {
    getAllClientPackageDetails({
      requestPayload: {
        filters: {
          active: true,
          department: PackageDepartmentEnum.DeepCleaning,
          sort: [SortEnum.Asc],
          sortBy: [PackageDetailSortByEnum.Duration],
        },
      },
    }).then((res) => {
      if (res.error || !res.data) {
        return;
      }
      setBookingState({
        packageDetails: res.data,
        packageDetailsFilter: {
          repeatEvery: [PackageRecurrenceEnum.AdHoc],
        },
        startDate: deepCleaningStartDate,
        endDate: deepCleaningEndDate,
      });
    });
  }, []);

  return (
    <View>
      <View className="flex flex-col gap-6">
        <View className="mx-4">
          <View className="mt-4 flex flex-row items-center gap-2">
            <ListHeading title="Reason for the service" icon={ChatCircleText} />
            <ServiceReasonTooltip />
          </View>

          <View className="pt-4">
            <View
              className="flex cursor-pointer flex-row items-center justify-between rounded-lg border border-secondary-border p-2"
              onClick={handleClickServiceReasonModal}
            >
              <Typography variant="label-sm">
                {creationReason || "Please select your reason"}
              </Typography>
              <CaretDown />
            </View>
          </View>
        </View>

        <View className="mx-4">
          <ListHeading
            title="Choose Cleaning Package"
            icon={Cube}
            label="Required"
          />
          <View className="mt-2 flex flex-col gap-2">
            <Typography variant="body-sm" color="foreground-intermediate">
              Select a package based on your home size for a thorough clean.
            </Typography>
            <Message
              variant="info"
              message="Disclaimer: Options apply to HDB and condominiums only. For landed properties, kindly contact us on WhatsApp."
              className="mt-4"
            />
          </View>
          <RadioGroup
            value={
              selectedPackageIds?.filter((id) =>
                currentPackageIDs.includes(id),
              )?.[0]
            }
            onValueChange={onChangePackage}
            className="pt-4"
          >
            {currentPackages.map((item) => {
              return (
                <ServiceRadioItem
                  key={item.title}
                  title={item.title}
                  subTitle={`$${item.unitValue * item.duration * item.numberOfCleaner}`}
                  description={item.description}
                  selected={
                    selectedPackageIds?.filter((id) =>
                      currentPackageIDs.includes(id),
                    )?.[0] === item.id
                  }
                  value={item.id}
                  showSparkle
                  onSelect={() => {
                    onChangePackage(item.id);
                  }}
                />
              );
            })}
          </RadioGroup>
        </View>

        <HelpCard
          name="deep-cleaning"
          subTitle="Let us match you with the perfect helper!"
        />

        {/* Additional Notes */}
        <View className="mx-4 flex flex-col gap-2">
          <View className="flex flex-row items-center gap-2">
            <ListHeading
              title="Additional Notes"
              icon={StackPlus}
              label="Optional"
            />
          </View>
          <View>
            <Textarea
              value={additionalNotes}
              className="rounded-xl bg-white"
              placeholder="Please state if there is any specific dirty area to be cleaned"
              onChangeText={(str) =>
                setBookingState({
                  additionalNotes: str,
                })
              }
            />
          </View>
        </View>
      </View>

      <ServiceReasonModal
        open={openServiceReasonModal}
        setOpen={setOpenServiceReasonModal}
        reason={creationReason || ""}
        setReason={(creationReason: string) =>
          setBookingState({
            creationReason,
            additionalNotes: creationReason,
          })
        }
      />
    </View>
  );
};

const ServiceReasonModal = ({
  open,
  setOpen,
  reason,
  setReason,
}: {
  open: boolean;
  reason: string;
  setOpen: (open: boolean) => void;
  setReason: (open: string) => void;
}) => {
  const ServiceReasons = ["Move In/Post Cleaning", "Move Out/End of Tenancy"];
  return (
    <BottomDrawerModal open={open} onOpenChange={() => setOpen(false)}>
      <View>
        <View className="flex flex-col gap-3">
          <Typography variant="label-sm">Select your reason</Typography>
          <View>
            <RadioGroup value={reason} onValueChange={setReason}>
              {ServiceReasons.map((item) => {
                return (
                  <Label htmlFor={item}>
                    <View
                      key={item}
                      className="cursor-pointer border-b border-secondary-border py-2"
                    >
                      <View
                        className="flex w-full flex-1 flex-row items-center justify-between"
                        onClick={() => setReason(item)}
                      >
                        <Typography
                          variant="body-sm"
                          color="foreground-intermediate"
                        >
                          {item}
                        </Typography>
                        <RadioGroupItem value={item} checkCircle id={item} />
                      </View>
                    </View>
                  </Label>
                );
              })}
            </RadioGroup>
          </View>
        </View>

        <Button
          className="mt-4 w-full"
          onClick={() => setOpen(false)}
          variant="primary"
          color="CTA"
        >
          Select
        </Button>
      </View>
    </BottomDrawerModal>
  );
};

const ServiceReasonTooltip = () => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Question size={22} />
      </DropdownMenuTrigger>

      <DropdownMenuContent className="-left-10 p-2 px-3">
        <View className="max-w-[311px]">
          <Typography variant="body-sm">
            Every deep cleaning has its own checklist, to provide you with our
            best service, we will need to know the purpose of the cleaning
          </Typography>
        </View>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
